import { FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Panel } from "../components/layout/Panel";
import { WarningPanel } from "../components/layout/WarningPanel";
import { SessionGatePanel } from "./SessionGatePanel";
import { useSession } from "./session";

export function LoginPage() {
  const location = useLocation();
  const { status, login, error, loading, refreshingSession, refreshSession, clearError } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loginWarnings, setLoginWarnings] = useState<string[]>([]);

  if (status === "authenticated") {
    const returnTo = (location.state as { from?: string } | null)?.from || "/planning";
    return <Navigate to={returnTo} replace />;
  }

  // Keep /login usable during transient session bootstrap failures so a fresh sign-in can recover.
  if (status === "checking") {
    return (
      <SessionGatePanel
        status={status}
        error={error}
        retrying={refreshingSession}
        shellClassName="login-shell"
      />
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    clearError();
    setLoginWarnings([]);
    try {
      const result = await login({ email, password, mfaCode });
      if (!result.ok) {
        setMfaRequired(result.mfa_required);
        setLoginWarnings(result.warnings);
      }
    } catch {
      setLoginWarnings([]);
    }
  }

  function handleCredentialChange(nextValue: string, update: (value: string) => void) {
    update(nextValue);
    if (mfaRequired || loginWarnings.length > 0 || mfaCode) {
      setMfaRequired(false);
      setMfaCode("");
      setLoginWarnings([]);
    }
  }

  function handleMfaCodeChange(nextValue: string) {
    setMfaCode(nextValue);
    if (loginWarnings.length > 0) {
      setLoginWarnings([]);
    }
  }

  return (
    <main className="login-shell login-shell-centered">
      <section className="login-hero">
        <div className="eyebrow">Inventory Intelligence</div>
        <h1>Plan inventory with confidence.</h1>
        <p>
          Run replenishment, procurement, operations, finance, and recommendation quality workflows
          from one clean operating system.
        </p>
        <ul className="feature-list">
          <li>Recommendation runs with quality and feedback visibility</li>
          <li>Operational system health, sync freshness, and readiness checks</li>
          <li>Production-safe deployment, preflight, backup, and recovery tooling</li>
          <li>Adaptive intelligence that earns more control as proven outcome data grows</li>
        </ul>
      </section>
      <Panel className="login-card glass-panel">
        <div className="login-card-header">
          <h2>Sign in</h2>
          <p>Owner sign-in</p>
          <p>Secure business planning and operations access.</p>
        </div>
        {status === "unavailable" && (
          <>
            <WarningPanel
              title="Session Check Warning"
              warnings={[error || "Session status is temporarily unavailable. You can still sign in."]}
            />
            <button type="button" className="button-secondary" onClick={() => void refreshSession()} disabled={refreshingSession}>
              {refreshingSession ? "Retrying session..." : "Retry Session Check"}
            </button>
          </>
        )}
        <form onSubmit={handleSubmit} className="login-form login-form-stacked">
          <label>
            Email
            <input
              value={email}
              onChange={(event) => handleCredentialChange(event.target.value, setEmail)}
              type="email"
              autoComplete="username"
              placeholder="owner@company.com"
              required
            />
          </label>
          <label>
            Password
            <input
              value={password}
              onChange={(event) => handleCredentialChange(event.target.value, setPassword)}
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              required
            />
          </label>
          {mfaRequired && (
            <label>
              MFA Code
              <input
                value={mfaCode}
                onChange={(event) => handleMfaCodeChange(event.target.value)}
                inputMode="numeric"
                pattern="[0-9]{6}"
                placeholder="123456"
              />
            </label>
          )}
          <button disabled={loading} type="submit">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <WarningPanel title="Login Warnings" warnings={loginWarnings} />
        {error && status !== "unavailable" && <p className="error">{error}</p>}
        <p className="muted small-text">
          For production onboarding, customers should explicitly accept the Privacy Policy, Terms of Service,
          and Intelligence Training & Improvement Addendum before account activation. Current draft policy docs
          live in <code>docs/privacy_policy.md</code>, <code>docs/terms_of_service.md</code>, and <code>docs/intelligence_training_addendum.md</code>.
        </p>
      </Panel>
    </main>
  );
}
